import { supabase } from '../../lib/supabase';

// Red de seguridad / señal de completitud para las generaciones de Interfaz V2.
// El front genera un `execution_id` (UUID) y lo manda al webhook; el backend lo
// escribe en CADA fila de `imagenes_generadas` — tanto las que salen bien como
// las que fallan (`has_error = true`). Eso permite:
//   • saber CUÁNDO terminó una tanda (contando filas, con o sin error) →
//     usado por el catálogo, que espera a que estén TODAS antes de mostrar;
//   • recuperar una generación cuando se pierde la respuesta del webhook
//     (error técnico / timeout / conexión caída) → usado como fallback en los
//     modos de imagen single (auto / anuncio / personalizada).
// Los callers filtran `has_error` según lo que necesiten.
//
// IMPORTANTE: solo devuelve filas "settled" — finales MOSTRABLES (`active =
// true`) o FALLIDAS (`has_error = true`). Las INTERMEDIAS (`active = false` +
// `has_error = false`, ej. el frame extra del multi-pose) quedan EXCLUIDAS: no
// se muestran (el historial filtra `active = true`) ni cuentan para la
// completitud. Por eso `expectedCount` = cantidad de imágenes FINALES esperadas.

export interface PolledGeneratedRow {
  /** `id` de la fila (uuid → string). */
  id: string;
  /** `variante_id` (uuid → string) para reconstruir la agrupación por producto
   *  del catálogo. Puede ser null. */
  variante_id: string | null;
  /** true si la generación de esa fila falló (igual ocupa lugar en el conteo
   *  de completitud, pero NO se muestra). */
  has_error: boolean;
  /** Taxonomía del backend cuando falló (policy_violation, provider_timeout,
   *  technical…) — los callers la mapean a copy mostrable con
   *  `mapErrorTypeToMessage` (censura ≠ error técnico). Null en filas ok o
   *  escritas por el camino viejo de n8n. */
  error_type: string | null;
  /** El `execution_id` de la fila: con varios ids en el poll, es lo que dice de QUÉ
   *  request salió cada imagen (el lote del agente atribuye cada fila a su ítem,
   *  ADR-2026-09-08-el-chat-trabaja-en-lote). */
  execution_id?: string | null;
}

// Tope por defecto del poll de imágenes (más corto que el del video: las
// imágenes vuelven en segundos). 5 min.
export const IMAGE_POLL_TIMEOUT_MS = 5 * 60 * 1000;
// Overrides para los modos ANUNCIO (gpt-5 en high): una imagen puede tardar
// varios minutos, así que tanto el tope total como la tolerancia entre filas
// tienen que ser más generosos que el default (calibrado para gaps de
// 30-60s). Sin esto, con cantidad_imagenes > 1 el poll cerraba la tanda con
// la primera imagen y el resto caía "después" al historial (ADR-578).
export const ANUNCIO_POLL_TIMEOUT_MS = 10 * 60 * 1000;
export const ANUNCIO_STABLE_ROUNDS = 60; // ~240s sin filas nuevas
const POLL_INTERVAL_MS = 4000;
// Rondas consecutivas sin filas nuevas para dar por terminada la tanda cuando
// NO se alcanza `expectedCount`. OJO con achicarla: el backend genera las
// imágenes de un mismo request multi-pose/multi-vista SECUENCIALMENTE
// (~30-60s por imagen), así que gaps de 30-60s entre filas son lo NORMAL de
// una tanda sana — con 3 rondas (12s) el catálogo cortaba por la mitad y las
// imágenes restantes quedaban solo en el historial (ADR-133). La señal
// principal de "no van a llegar más filas" es que el caller DESCUENTE de
// `expectedCount` los requests fallidos (getter); la estabilidad queda como
// red de seguridad para el caso raro (request aceptado en HTTP que nunca
// escribe filas), así que puede ser generosa.
const STABLE_ROUNDS = 23; // ~92s sin filas nuevas

/**
 * Poolea `imagenes_generadas` por `execution_id` y devuelve TODAS las filas de
 * la tanda (ok + error), sin filtrar. El caller decide qué hacer con
 * `has_error`.
 *
 * Resuelve cuando:
 *  - `expectedCount` (si se pasa) se alcanza contando TODAS las filas (ok +
 *    error) → la tanda terminó, OR
 *  - el conteo se mantiene estable `STABLE_ROUNDS` rondas (algún request no
 *    escribió su fila) → terminó lo que iba a terminar, OR
 *  - se agota `timeoutMs` → devuelve lo que haya.
 *
 * Vacío = no apareció nada dentro del tope → el caller decide.
 */
export async function pollGeneratedByExecutionId(opts: {
  /**
   * Uno o VARIOS `execution_id`.
   *
   * Se aceptan varios porque los modos de catálogo disparan una llamada por
   * (producto × vista) y cada una necesita su PROPIO id: cuando compartían uno
   * solo, las ejecuciones paralelas de `Generar anuncio V3` se veían las filas
   * entre sí —ese workflow busca por `execution_id`— y eso disparaba un fan-out
   * que generaba imágenes de más y las cobraba. Ver ADR-590.
   */
  executionId: string | string[];
  companyId: string;
  timeoutMs?: number;
  /** Cantidad de imágenes FINALES esperadas. Acepta un getter para que el
   *  caller pueda BAJARLA mientras el poll corre — ej. el catálogo descuenta
   *  las cards de un request que falló a nivel HTTP y nunca va a escribir sus
   *  filas (ADR-133). Si el getter llega a 0, el poll resuelve al toque con
   *  lo que haya. */
  expectedCount?: number | (() => number);
  /** Permite cancelar el poll desde afuera (ej. cuando otro camino —la
   *  respuesta del webhook— ya entregó el resultado). */
  signal?: AbortSignal;
  /** Rondas sin filas nuevas para cortar por estabilidad. Default
   *  STABLE_ROUNDS (~92s) — subirlo en flujos donde el gap normal entre
   *  imágenes es mayor (anuncio/gpt-5: ANUNCIO_STABLE_ROUNDS). */
  stableRounds?: number;
  /**
   * Se llama cada vez que la cuenta de filas CAMBIA, con todas las filas hasta ese
   * momento — para ir pintando lo que llega mientras el resto sigue generando (la grilla
   * del lote del agente). No se llama al final: el resultado de la promesa ya es eso.
   */
  onProgreso?: (rows: PolledGeneratedRow[]) => void;
}): Promise<PolledGeneratedRow[]> {
  const { executionId, companyId, expectedCount, signal } = opts;
  const timeoutMs = opts.timeoutMs ?? IMAGE_POLL_TIMEOUT_MS;
  const stableRoundsMax = opts.stableRounds ?? STABLE_ROUNDS;
  const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));
  const maxAttempts = Math.ceil(timeoutMs / POLL_INTERVAL_MS);

  let rows: PolledGeneratedRow[] = [];
  let lastCount = 0;
  let stable = 0;
  let ultimoAvisado = 0;

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    if (signal?.aborted) return rows;
    try {
      const ids = Array.isArray(executionId) ? executionId : [executionId];
      const { data } = await supabase
        .from('imagenes_generadas')
        .select('id, variante_id, has_error, error_type, execution_id')
        .in('execution_id', ids)
        .eq('id_company', companyId)
        // Solo filas "settled": finales MOSTRABLES (`active = true`) o FALLIDAS
        // (`has_error = true`). Excluye las INTERMEDIAS (`active = false` +
        // `has_error = false`) — ej. el frame de más que el backend genera en
        // multi-pose — que no se muestran (el historial también filtra
        // `active = true`) ni deben contar para la completitud.
        .or('active.eq.true,has_error.eq.true');
      rows = (data ?? []) as PolledGeneratedRow[];
    } catch {
      // Blip transitorio de red/DB → mantenemos `rows` y reintentamos en la
      // próxima ronda (NUNCA lanzamos: la carrera depende de que el poll
      // siempre resuelva).
    }

    if (opts.onProgreso && rows.length !== ultimoAvisado) {
      ultimoAvisado = rows.length;
      try {
        opts.onProgreso(rows);
      } catch {
        /* un error al pintar no corta el poll */
      }
    }

    // Completitud por cantidad esperada: cuenta TODAS las filas (ok + error).
    // Con getter, el esperado puede BAJAR entre rondas (fallos HTTP del
    // caller); si queda en 0 no hay nada que esperar y salimos con lo que hay.
    const expected =
      typeof expectedCount === 'function' ? expectedCount() : expectedCount;
    if (expected !== undefined && rows.length >= expected) return rows;
    if (rows.length > 0) {
      if (rows.length === lastCount) {
        stable += 1;
        if (stable >= stableRoundsMax) return rows;
      } else {
        stable = 0;
        lastCount = rows.length;
      }
    }
    await sleep(POLL_INTERVAL_MS);
  }
  return rows;
}
